// RecipeDrawerAI.jsx
import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { Drawer, Input } from "antd";
import { Button } from "react-bootstrap";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import Loader from "../../components/Common/Loader";
import { nutrientUnits } from "../../utils/Constant/Constant";
import ToastNotification from "../../components/Common/ToastNotification";

const RecipeDrawerAI = ({
  visible,
  onClose,
  recipe,
  onRecipeUpdate,
  isCurrentlyEditing,
}) => {
  const { trans } = useCustomTranslation();
  const [loadingImage, setLoadingImage] = useState(false);
  const [editableRecipe, setEditableRecipe] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [toastConfig, setToastConfig] = useState(null);

  // Initialize editable recipe when recipe prop changes
  useEffect(() => {
    if (recipe) {
      setEditableRecipe({
        ...recipe,
        totalTime: recipe.prepTime || recipe.totalTime || "",
        ingredientLines: [...(recipe.ingredientLines || [])],
        ingredients: [...(recipe.ingredients || [])],
        nutrientsPerServing: { ...(recipe.nutrientsPerServing || {}) },
        macroPercentages: {
          carbs: recipe.relativeCalories?.carbs
            ? (recipe.relativeCalories.carbs * 100).toFixed(1)
            : "0",
          protein: recipe.relativeCalories?.protein
            ? (recipe.relativeCalories.protein * 100).toFixed(1)
            : "0",
          fat: recipe.relativeCalories?.fat
            ? (recipe.relativeCalories.fat * 100).toFixed(1)
            : "0",
        },
      });
      // Reset edit mode when recipe changes
      setIsEditMode(false);
    }
  }, [recipe]);

  // Reset edit mode if not currently editing this recipe
  useEffect(() => {
    if (!isCurrentlyEditing) {
      setIsEditMode(false);
    }
  }, [isCurrentlyEditing]);

  const hasRecipe = !!recipe;
  const MISSING_NUTRIENT_FALLBACK = "0";

  // Validate input based on field type
  const validateInput = (field, value) => {
    // For quantity fields (metric/imperial), only allow numbers and decimals
    if (field.includes("quantity")) {
      return /^\d*\.?\d*$/.test(value);
    }
    // For measure fields (metric/imperial), only allow letters and spaces
    if (field.includes("measure")) {
      return /^[a-zA-Z\s]*$/.test(value);
    }
    return true;
  };

  // Handle ingredient quantity changes for both metric and imperial
  const handleIngredientChange = (index, field, value) => {
    // Validate input before updating
    if (!validateInput(field, value)) {
      return;
    }

    // Handle auto "0" logic for quantity fields
    let finalValue = value;

    // If field is a quantity field (metric or imperial)
    if (field === "quantityinMetric" || field === "quantityinImperial") {
      // If user clears the field, set 0
      if (finalValue === "") {
        finalValue = "0";
      }
      // Remove leading zeroes if the user types a number like "01", "002", etc.
      else if (/^0\d+/.test(finalValue)) {
        finalValue = finalValue.replace(/^0+/, "");
      }
    }

    setEditableRecipe((prev) => {
      const updatedIngredients = [...(prev.ingredients || [])];

      // Update the specific field in the ingredients array
      if (updatedIngredients[index]) {
        updatedIngredients[index] = {
          ...updatedIngredients[index],
          [field]: finalValue,
        };
      }

      // Rebuild ingredientLines from updated ingredients array
      const updatedIngredientLines = updatedIngredients.map((ing) =>
        `${ing.quantityinMetric || ""} ${ing.measureinMetric || ""} ${
          ing.ingredient || ""
        }`.trim()
      );

      return {
        ...prev,
        ingredients: updatedIngredients,
        ingredientLines: updatedIngredientLines,
      };
    });
  };

  // Handle nutrient changes
  const handleNutrientChange = (key, value) => {
    setEditableRecipe((prev) => {
      const currentValue = String(prev.nutrientsPerServing?.[key] ?? "");
      const input = String(value ?? "").trim();

      // Extract unit from input (letters or % at the end)
      const inputUnitMatch = input.match(/([a-zA-Z%]+)$/);
      let unit = inputUnitMatch ? inputUnitMatch[1] : "";

      // Extract numeric part
      const numMatch = input.match(/-?\d*\.?\d+/);
      let numStr = numMatch ? numMatch[0] : "";

      // If input has no unit, fallback to existing unit
      if (!unit) {
        const curUnitMatch = currentValue.match(/([a-zA-Z%]+)$/);
        unit = curUnitMatch ? curUnitMatch[1] : "";
      }

      // If no numeric part, set to 0 + unit
      if (!numStr) {
        return {
          ...prev,
          nutrientsPerServing: {
            ...prev.nutrientsPerServing,
            [key]: `0 ${unit}`,
          },
        };
      }

      // Normalize numeric part: parseFloat removes leading zeros
      const normalizedNumber = String(parseFloat(numStr));

      return {
        ...prev,
        nutrientsPerServing: {
          ...prev.nutrientsPerServing,
          [key]: `${normalizedNumber} ${unit}`,
        },
      };
    });
  };

  // Handle percentage changes for macros (carbs, protein, fat)
  const handlePercentageChange = (macro, value) => {
    // Allow only numbers and decimals
    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }

    let finalValue = value;

    // Auto "0" logic
    if (finalValue === "") {
      finalValue = "0";
    } else if (/^0\d+/.test(finalValue)) {
      finalValue = finalValue.replace(/^0+/, "");
    }

    setEditableRecipe((prev) => ({
      ...prev,
      macroPercentages: {
        ...prev.macroPercentages,
        [macro]: finalValue,
      },
    }));
  };

  const handleSaveChanges = () => {
    const { carbs, protein, fat } = editableRecipe.macroPercentages || {};

    const carbValue = Number(carbs) || 0;
    const proteinValue = Number(protein) || 0;
    const fatValue = Number(fat) || 0;
    const total = carbValue + proteinValue + fatValue;

    // Validation messages
    if (carbValue <= 0) {
      setToastConfig({
        type: "error",
        message: "Carbs percentage must be greater than 0",
        heading: "Failed",
      });
      setTimeout(() => setToastConfig(null), 3000);
      return;
    }

    if (proteinValue <= 0) {
      setToastConfig({
        type: "error",
        message: "Protein percentage must be greater than 0",
        heading: "Failed",
      });
      setTimeout(() => setToastConfig(null), 3000);
      return;
    }

    if (fatValue <= 0) {
      setToastConfig({
        type: "error",
        message: "Fat percentage must be greater than 0",
        heading: "Failed",
      });
      setTimeout(() => setToastConfig(null), 3000);
      return;
    }

    // eslint-disable-next-line eqeqeq
    if (total != 100) {
      setToastConfig({
        type: "error",
        message: "Total of carbs, protein, and fat should be 100%",
        heading: "Failed",
      });
      setTimeout(() => setToastConfig(null), 3000);
      return;
    }

    // If validation passes, save changes
    if (onRecipeUpdate) {
      onRecipeUpdate(editableRecipe);
    }
    setIsEditMode(false);
  };

  // Cancel changes and revert to original
  const handleCancelChanges = () => {
    setEditableRecipe({
      ...recipe,
      ingredientLines: [...(recipe.ingredientLines || [])],
      ingredients: [...(recipe.ingredients || [])], // Add ingredients array
      // nutrientsPerServing: { ...(recipe.nutrientsPerServing || {}) },
      macroPercentages: {
        carbs: recipe.relativeCalories?.carbs
          ? (recipe.relativeCalories.carbs * 100).toFixed(1)
          : "0",
        protein: recipe.relativeCalories?.protein
          ? (recipe.relativeCalories.protein * 100).toFixed(1)
          : "0",
        fat: recipe.relativeCalories?.fat
          ? (recipe.relativeCalories.fat * 100).toFixed(1)
          : "0",
      },
    });
    setIsEditMode(false);
  };

  // Extract numeric value from string (e.g., "32g" -> 32)
  const extractNumericValue = (value) => {
    if (!value) return "";
    const match = String(value).match(/[\d.]+/);
    return match ? match[0] : "";
  };

  // Format value with unit for display
  const formatValueWithUnit = (
    val,
    unit,
    fallback = MISSING_NUTRIENT_FALLBACK
  ) => {
    if (val === null || val === undefined) return `${fallback} ${unit}`;
    const strVal = String(val).trim();
    if (strVal === "" || strVal.toLowerCase() === "nan")
      return `${fallback} ${unit}`;
    if (/[a-zA-Z]/.test(strVal)) return strVal;
    return `${strVal} ${unit}`;
  };

  // Use stored percentages instead of calculating from grams
  const macroRatios = useMemo(() => {
    if (editableRecipe?.macroPercentages) {
      return {
        carbs: (Number(editableRecipe.macroPercentages.carbs) || 0) / 100,
        protein: (Number(editableRecipe.macroPercentages.protein) || 0) / 100,
        fat: (Number(editableRecipe.macroPercentages.fat) || 0) / 100,
      };
    }

    // Fallback to original calculation if no manual percentages
    const carbsGrams =
      Number(extractNumericValue(editableRecipe?.nutrientsPerServing?.carbs)) ||
      0;
    const proteinGrams =
      Number(
        extractNumericValue(editableRecipe?.nutrientsPerServing?.protein)
      ) || 0;
    const fatGrams =
      Number(extractNumericValue(editableRecipe?.nutrientsPerServing?.fat)) ||
      0;

    const totalCalories = proteinGrams * 4 + carbsGrams * 4 + fatGrams * 9;
    if (!totalCalories) return { carbs: 0, protein: 0, fat: 0 };

    return {
      carbs: (carbsGrams * 4) / totalCalories,
      protein: (proteinGrams * 4) / totalCalories,
      fat: (fatGrams * 9) / totalCalories,
    };
  }, [editableRecipe]);

  const dotColor = {
    carbs: "#89008e",
    protein: "#574dec",
    fat: "#dceb00",
  };

  const formatNutrientKey = (key) =>
    key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());

  return (
    <Drawer
      title={
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span>Recipe Details</span>
          <div>
            {!isEditMode ? (
              <Button
                type="primary"
                onClick={() => setIsEditMode(true)}
                className="saveButton pl-4 pr-4"
              >
                Edit
              </Button>
            ) : (
              <div>
                <Button
                  variant="outline-primary"
                  onClick={handleCancelChanges}
                  className="cancel-button mr-2 pl-1 pr-1"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={handleSaveChanges}
                  className="saveButton pl-4 pr-4"
                >
                  Save
                </Button>
              </div>
            )}
          </div>
        </div>
      }
      placement="right"
      width="30%"
      onClose={onClose}
      open={visible}
      className="p-3"
    >
      {!hasRecipe ? (
        <Loader />
      ) : (
        <div>
          {/* Main Recipe Image */}
          {editableRecipe?.mainImage && (
            <img
              className="image-card-drawer"
              alt={editableRecipe.name || "Recipe"}
              src={editableRecipe.mainImage}
              onLoad={() => setLoadingImage(false)}
              onError={() => setLoadingImage(false)}
              style={{ display: loadingImage ? "none" : "block" }}
            />
          )}

          {/* Recipe Title */}
          <h3 className="drawer-recipe-name text-gray-800">
            {editableRecipe?.name}
          </h3>

          <div className="detail-container-drawer">
            <hr />
            {/* Basic details */}
            <div className="d-flex">
              <p className="detail-time-recipe">{editableRecipe?.totalTime}</p>
              <p className="detail-time-recipe">
                {`${editableRecipe?.numberOfServings ?? 0}`}
              </p>
              <p className="detail-time-recipe">
                {`${editableRecipe?.ingredientLines?.length ?? 0} ${trans(
                  "recipes.ingredients"
                )}`}
              </p>
            </div>

            <hr className="mt-0" />

            {/* Editable Ingredients list - metric and imperial fields */}
            <h4>{trans("recipes.Ingredients")}</h4>
            <ul className="blue-dot-list">
              {(editableRecipe?.ingredientLines || []).map((item, idx) => {
                const ingredient = editableRecipe.ingredients?.[idx];
                const ingredientName = ingredient?.ingredient || "";

                return (
                  <li key={idx} className="fs-5 d-flex align-items-center">
                    {isEditMode ? (
                      <div className="mb-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span className="fw-bold">{ingredientName}</span>
                        </div>
                        <div className="row g-2">
                          <div className="col-6">
                            <label className="form-label small">Metric</label>
                            <div className="d-flex gap-1">
                              <Input
                                value={ingredient?.quantityinMetric || ""}
                                onChange={(e) =>
                                  handleIngredientChange(
                                    idx,
                                    "quantityinMetric",
                                    e.target.value
                                  )
                                }
                                size="small"
                                placeholder="Qty"
                                style={{ width: "60px" }}
                              />
                              <Input
                                value={ingredient?.measureinMetric || ""}
                                onChange={(e) =>
                                  handleIngredientChange(
                                    idx,
                                    "measureinMetric",
                                    e.target.value
                                  )
                                }
                                size="small"
                                placeholder="Unit"
                                style={{ width: "60px" }}
                              />
                            </div>
                          </div>
                          <div className="col-6">
                            <label className="form-label small">Imperial</label>
                            <div className="d-flex gap-1">
                              <Input
                                value={ingredient?.quantityinImperial || ""}
                                onChange={(e) =>
                                  handleIngredientChange(
                                    idx,
                                    "quantityinImperial",
                                    e.target.value
                                  )
                                }
                                size="small"
                                placeholder="Qty"
                                style={{ width: "60px" }}
                              />
                              <Input
                                value={ingredient?.measureinImperial || ""}
                                onChange={(e) =>
                                  handleIngredientChange(
                                    idx,
                                    "measureinImperial",
                                    e.target.value
                                  )
                                }
                                size="small"
                                placeholder="Unit"
                                style={{ width: "60px" }}
                              />
                            </div>
                          </div>
                        </div>
                        <hr />
                      </div>
                    ) : (
                      <div>
                        <div>{item}</div>
                        {/* {ingredient && (
                          <small className="text-muted">
                            Imperial: {ingredient.quantityinImperial}{" "}
                            {ingredient.measureinImperial}
                          </small>
                        )} */}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {/* Instructions list */}
            <h4>{trans("recipes.instructions")}</h4>
            <ul className="blue-dot-list">
              {(editableRecipe?.instructions || []).map((item, idx) => (
                <li key={idx} className="fs-5">
                  {item}
                </li>
              ))}
            </ul>

            <hr />
            {/* Nutrition Section */}
            <h4>{trans("recipes.nutritionPerServing")}</h4>

            {/* Macro split progress bar */}
            <div className="mt-2 progress-bar-recipe mb-3">
              <div
                className="bar-carb"
                style={{ width: `${(macroRatios.carbs || 0) * 100}%` }}
              />
              <div
                className="bar-protein"
                style={{ width: `${(macroRatios.protein || 0) * 100}%` }}
              />
              <div
                className="bar-fat"
                style={{ width: `${(macroRatios.fat || 0) * 100}%` }}
              />
            </div>

            {/* Manually Editable Macro indicators with separate percentage inputs */}
            {["carbs", "protein", "fat"].map((macro) => (
              <React.Fragment key={macro}>
                <div className="indicator-nutrition d-flex align-items-center">
                  <div
                    className="rounded-circle ml-2 mr-2"
                    style={{
                      backgroundColor: dotColor[macro],
                      width: 15,
                      height: 15,
                    }}
                  />
                  <span className="fs-5">
                    {trans(`recipes.${macro}`)} (
                    {isEditMode ? (
                      <Input
                        value={editableRecipe?.macroPercentages?.[macro] || "0"}
                        onChange={(e) =>
                          handlePercentageChange(macro, e.target.value)
                        }
                        suffix="%"
                        size="small"
                        style={{ width: 80 }}
                      />
                    ) : (
                      `${
                        editableRecipe?.macroPercentages?.[macro] ||
                        ((macroRatios?.[macro] || 0) * 100).toFixed(1)
                      }%`
                    )}
                    )
                  </span>
                  <div className="ml-auto mb-0">
                    {isEditMode ? (
                      <Input
                        value={extractNumericValue(
                          editableRecipe?.nutrientsPerServing?.[macro]
                        )}
                        onChange={(e) =>
                          handleNutrientChange(macro, `${e.target.value}g`)
                        }
                        suffix="g"
                        size="small"
                        style={{ width: 80 }}
                      />
                    ) : (
                      <p className="mb-0">
                        {formatValueWithUnit(
                          editableRecipe?.nutrientsPerServing?.[macro],
                          "g"
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <hr />
              </React.Fragment>
            ))}

            {/* Other editable nutrients */}
            <ul>
              {Object.entries(editableRecipe?.nutrientsPerServing || {})
                .filter(([key]) => !["carbs", "protein", "fat"].includes(key))
                .map(([key, value]) => {
                  const formattedKey = formatNutrientKey(key);
                  const unit = nutrientUnits[key] || "g";
                  return (
                    <li key={key} className="p-0 m-0">
                      <div className="d-flex justify-content-between align-items-center">
                        <p>{formattedKey}</p>
                        <div>
                          {isEditMode ? (
                            <Input
                              value={extractNumericValue(value)}
                              onChange={(e) =>
                                handleNutrientChange(
                                  key,
                                  `${e.target.value}${unit}`
                                )
                              }
                              suffix={unit}
                              size="small"
                              style={{ width: 80 }}
                            />
                          ) : (
                            <p>{formatValueWithUnit(value, unit)}</p>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
      )}
      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </Drawer>
  );
};

RecipeDrawerAI.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  recipe: PropTypes.object,
  onRecipeUpdate: PropTypes.func,
  isCurrentlyEditing: PropTypes.bool,
};

export default RecipeDrawerAI;